import React, { useEffect, useState, useCallback } from "react";
import { Box, Typography, Button, TextField, Grid } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useIntl } from "react-intl";
import { fetchMemberCategory, fetchMemberType, createMember } from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import { COMPANY_ID } from "../../config/appConfig";

export default function MemberCategoryMemberCreatePage() {
  const { businessDomainId, categoryId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });

  const [memberClass, setMemberClass] = useState("");
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    memberNumber: "",
    memberDescription: "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const listPath = `/strategy/${businessDomainId}/member-categories/${categoryId}/members`;

  const loadMemberClass = useCallback(async () => {
    try {
      const cat = await fetchMemberCategory(categoryId);
      if (cat.memberTypeId) {
        const mt = await fetchMemberType(cat.memberTypeId);
        setMemberClass(mt.memberClass || "");
      }
    } catch (e) { notify.apiError(e); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId]);

  useEffect(() => { loadMemberClass(); }, [loadMemberClass]);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const validate = () => {
    const next = {};
    if (!form.firstName.trim()) next.firstName = t("message.error.required");
    if (!form.lastName.trim()) next.lastName = t("message.error.required");
    if (!form.email.trim()) next.email = t("message.error.required");
    if (!form.password.trim()) next.password = t("message.error.required");
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      await createMember({
        companyId: COMPANY_ID,
        memberCategoryId: categoryId,
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        password: form.password,
        memberNumber: form.memberNumber,
        memberDescription: form.memberDescription,
        memberClass,
      });
      notify.success(t("message.create.success"));
      navigate(listPath, { replace: true });
    } catch (e) { notify.apiError(e); }
    finally { setSubmitting(false); }
  };

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6">{t("member.create.title")}</Typography>
      </Box>
      <Grid container spacing={2} sx={{ maxWidth: 600 }}>
        <Grid size={12} md={6}>
          <TextField
            label={t("member.lastName")}
            value={form.lastName}
            onChange={handleChange("lastName")}
            error={!!errors.lastName}
            helperText={errors.lastName}
            fullWidth
            required
          />
        </Grid>
        <Grid size={12} md={6}>
          <TextField
            label={t("member.firstName")}
            value={form.firstName}
            onChange={handleChange("firstName")}
            error={!!errors.firstName}
            helperText={errors.firstName}
            fullWidth
            required
          />
        </Grid>
        <Grid size={12}>
          <TextField
            label={t("member.email")}
            type="email"
            value={form.email}
            onChange={handleChange("email")}
            error={!!errors.email}
            helperText={errors.email}
            fullWidth
            required
          />
        </Grid>
        <Grid size={12}>
          <TextField
            label={t("member.password")}
            type="password"
            value={form.password}
            onChange={handleChange("password")}
            error={!!errors.password}
            helperText={errors.password}
            fullWidth
            required
          />
        </Grid>
        <Grid size={12} md={6}>
          <TextField
            label={t("member.memberNumber")}
            value={form.memberNumber}
            onChange={handleChange("memberNumber")}
            fullWidth
          />
        </Grid>
        <Grid size={12}>
          <TextField
            label={t("common.description")}
            value={form.memberDescription}
            onChange={handleChange("memberDescription")}
            multiline
            rows={3}
            fullWidth
          />
        </Grid>
      </Grid>
      <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
          {t("common.save")}
        </Button>
        <Button variant="outlined" onClick={() => navigate(`/strategy/${businessDomainId}/member-categories/${categoryId}/members`)}>{t("common.cancel")}</Button>
      </Box>
    </Box>
  );
}
